import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Calendar, CheckCircle, Clock, ArrowLeftRight, XCircle, FileText, ChevronDown, ChevronUp, DollarSign, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const PAYMENT_STATUS_STYLES = {
  "Current Week": { bg: "bg-[#2F5D8A]/10", text: "text-[#2F5D8A]", border: "border-[#2F5D8A]/30", dot: "bg-[#2F5D8A]" },
  "Pending Payment": { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", dot: "bg-amber-500" },
  "Paid": { bg: "bg-[#D8E8D4]", text: "text-[#4A7A56]", border: "border-[#A7C4A0]", dot: "bg-[#4A7A56]" },
};

const ACTION_COLORS = {
  Completed: "bg-[#D8E8D4] text-[#4A7A56] border-[#A7C4A0]",
  Hold: "bg-[#EDE5D6] text-[#7A6535] border-[#D1A054]",
  Returned: "bg-[#E8D4D4] text-[#8B4545] border-[#C07070]",
  "Not Done": "bg-gray-100 text-gray-500 border-gray-300",
  "No Show": "bg-red-50 text-red-600 border-red-200",
};

const InspectorWeeklyClosingPage = ({ Layout }) => {
  const navigate = useNavigate();
  const [data, setData] = useState({ weeks: [], inspector_name: "" });
  const [loading, setLoading] = useState(true);
  const [expandedWeek, setExpandedWeek] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/daily-closings/my-weekly-closing`);
      setData(res.data);
      if (res.data.weeks?.length > 0) setExpandedWeek(0);
    } catch (err) {
      console.error("Failed to fetch weekly closing:", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatWeekLabel = (start, end) => {
    try {
      const s = new Date(start + "T00:00:00");
      const e = new Date(end + "T00:00:00");
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return `${months[s.getMonth()]} ${s.getDate()} - ${months[e.getMonth()]} ${e.getDate()}, ${e.getFullYear()}`;
    } catch { return `${start} - ${end}`; }
  };

  const formatDayLabel = (dateStr) => {
    try {
      const d = new Date(dateStr + "T00:00:00");
      const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      return `${days[d.getDay()]} ${d.getMonth() + 1}/${d.getDate()}`;
    } catch { return dateStr; }
  };

  return (
    <Layout>
      <div className="space-y-5 max-w-5xl mx-auto" data-testid="inspector-weekly-closing-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-[#1F2937] flex items-center gap-2">
              <Calendar className="h-5 w-5 text-[#2F5D8A]" />
              Weekly Closing
            </h1>
            <p className="text-gray-400 text-sm mt-0.5">
              {data.inspector_name ? `${data.inspector_name} — ` : ""}Tue-Mon closing periods
            </p>
          </div>
          <button
            onClick={() => navigate("/day-closing")}
            className="px-3 py-2 bg-white border border-gray-200 text-gray-600 text-xs font-medium rounded-lg hover:border-[#2F5D8A]/40 hover:text-[#2F5D8A] transition-colors"
            data-testid="go-to-daily-btn"
          >
            <FileText className="h-3.5 w-3.5 inline mr-1" />
            Day Closing
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2F5D8A]" />
          </div>
        ) : data.weeks.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-100" data-testid="no-weeks-message">
            <Calendar className="h-10 w-10 mx-auto mb-3 text-gray-200" />
            <p className="text-sm font-medium text-gray-400">No closing records found</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="weekly-cards-container">
            {data.weeks.map((week, idx) => {
              const ps = PAYMENT_STATUS_STYLES[week.payment_status] || PAYMENT_STATUS_STYLES["Pending Payment"];
              const isExpanded = expandedWeek === idx;
              const s = week.summary;
              const weekLabel = idx === 0 ? "Current Week" : idx === 1 ? "Previous Week" : "2 Weeks Ago";

              // Group orders by route_date for the detail view
              const ordersByDate = {};
              (week.orders || []).forEach(o => {
                const rd = o.route_date || "Unknown";
                if (!ordersByDate[rd]) ordersByDate[rd] = [];
                ordersByDate[rd].push(o);
              });
              const sortedDates = Object.keys(ordersByDate).sort();

              return (
                <div
                  key={idx}
                  className={`bg-white rounded-xl border transition-all ${isExpanded ? "border-[#2F5D8A]/30 shadow-sm" : "border-gray-200"}`}
                  data-testid={`weekly-card-${idx}`}
                >
                  {/* Card Header — always visible */}
                  <button
                    onClick={() => setExpandedWeek(isExpanded ? null : idx)}
                    className="w-full text-left p-4"
                    data-testid={`weekly-card-toggle-${idx}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">{weekLabel}</span>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${ps.bg} ${ps.text} ${ps.border}`} data-testid={`payment-status-${idx}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${ps.dot}`}></span>
                            {week.payment_status}
                          </span>
                        </div>
                        <p className="text-sm font-semibold text-[#1F2937]" data-testid={`week-range-${idx}`}>
                          {formatWeekLabel(week.week_start, week.week_end)}
                        </p>
                        <div className="flex items-center gap-1.5 mt-1">
                          <DollarSign className="h-3 w-3 text-gray-400" />
                          <span className="text-xs text-gray-500" data-testid={`payment-date-${idx}`}>
                            Payment: <span className="font-medium text-[#1F2937]">{week.payment_date_display}</span>
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-2xl font-bold text-[#2F5D8A]" data-testid={`total-orders-${idx}`}>{s.total}</p>
                          <p className="text-[9px] text-gray-400 uppercase font-medium">orders</p>
                        </div>
                        {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                      </div>
                    </div>

                    {/* Summary counters row */}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {[
                        { label: "Completed", value: s.Completed, icon: CheckCircle, color: "text-[#4A7A56] bg-[#D8E8D4]/60" },
                        { label: "Hold", value: s.Hold, icon: Clock, color: "text-[#7A6535] bg-[#EDE5D6]/60" },
                        { label: "Returned", value: s.Returned, icon: ArrowLeftRight, color: "text-[#8B4545] bg-[#E8D4D4]/60" },
                        { label: "Not Done", value: s["Not Done"], icon: XCircle, color: "text-gray-500 bg-gray-100" },
                        { label: "Follow-Up", value: s["Follow-Up"], icon: AlertTriangle, color: "text-red-600 bg-red-50" },
                      ].map(({ label, value, icon: Icon, color }) => (
                        <div key={label} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg ${color}`} data-testid={`count-${label.toLowerCase().replace(/[\s-]/g, '_')}-${idx}`}>
                          <Icon className="h-3 w-3" />
                          <span className="text-xs font-bold">{value}</span>
                          <span className="text-[10px] font-medium opacity-70">{label}</span>
                        </div>
                      ))}
                    </div>
                  </button>

                  {/* Expanded detail — orders grouped by date */}
                  {isExpanded && (
                    <div className="border-t border-gray-100" data-testid={`weekly-detail-${idx}`}>
                      {week.orders.length === 0 ? (
                        <div className="p-6 text-center text-sm text-gray-400">No orders closed this week</div>
                      ) : (
                        <div>
                          {sortedDates.map(dateStr => (
                            <div key={dateStr}>
                              <div className="px-4 py-2 bg-gray-50/80 border-b border-gray-100">
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{formatDayLabel(dateStr)}</span>
                                <span className="text-[10px] text-gray-300 ml-2">{ordersByDate[dateStr].length} orders</span>
                              </div>
                              <div className="divide-y divide-gray-50">
                                {ordersByDate[dateStr].map((o, oidx) => (
                                  <div
                                    key={oidx}
                                    className="px-4 py-2.5 hover:bg-gray-50/40 transition-colors cursor-pointer flex items-center justify-between gap-2"
                                    onClick={() => o.order_id && navigate(`/order/${o.order_id}`)}
                                    data-testid={`weekly-order-${o.order_number || oidx}`}
                                  >
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-semibold text-[#1F2937]">{o.order_number || "-"}</span>
                                        <span className="text-[10px] text-gray-400">{o.company || ""}</span>
                                      </div>
                                      <p className="text-xs text-gray-500 truncate">{o.address || "-"}{o.city ? `, ${o.city}` : ""}</p>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      {o.follow_up && (
                                        <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-50 text-red-600 border border-red-200" data-testid={`followup-tag-${o.order_number}`}>F/U</span>
                                      )}
                                      <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${ACTION_COLORS[o.action] || ACTION_COLORS["Not Done"]}`}>
                                        {o.action || "-"}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default InspectorWeeklyClosingPage;
